import { Alert, Drawer } from "antd";
import { lazy, Suspense, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../hooks/useNotification";
import { useLazyGetPrePaymentByIdQuery } from "../../../store/apiSlice/invoiceApiSlice";
import {
  PrepaymentListDrawerProvider,
  usePrepaymentListDrawer,
} from "../../prepayment/components/PrepaymentListDrawerContext";
import { PortalHeader } from "../../../components/molecules/Header";
import { DRAWER_WIDTH } from "../orderDesign/drawerWidths";
import {
  GraphDrawerBody,
  GraphDrawerTitle,
  RelationshipGraphDrawerTheme,
} from "./relationshipGraph.styles";
import { useRelationshipGraph } from "./useRelationshipGraph";

const RelationshipGraph = lazy(() =>
  import("./RelationshipGraph").then(module => ({ default: module.RelationshipGraph })),
);

/**
 * @param {{
 *   open: boolean,
 *   onClose: function,
 *   anchor: {type: string, id: number, label?: string}|null,
 *   onOpenFiles?: function,
 *   onOpenTasks?: function,
 * }} props
 */
export function RelationshipGraphDrawer(props) {
  return (
    <PrepaymentListDrawerProvider>
      <RelationshipGraphDrawerContent {...props} />
    </PrepaymentListDrawerProvider>
  );
}

function RelationshipGraphDrawerContent({ open, onClose, anchor, onOpenFiles, onOpenTasks }) {
  const navigate = useNavigate();
  const notify = useNotification();
  const prepaymentDrawer = usePrepaymentListDrawer();
  const [fetchPrepayment] = useLazyGetPrePaymentByIdQuery();

  const openPrepayment = useCallback(
    async id => {
      try {
        const prepayment = await fetchPrepayment(id).unwrap();
        prepaymentDrawer?.openPayment(prepayment);
      } catch (error) {
        notify({
          type: "error",
          message: error?.data?.detail || "Could not open this prepayment.",
        });
      }
    },
    [fetchPrepayment, notify, prepaymentDrawer],
  );

  const handlers = {
    onOpenFiles,
    onOpenTasks,
    onOpenPrepayment: openPrepayment,
    onOpenRecord: path => {
      onClose();
      navigate(path);
    },
  };
  const {
    flowNodes,
    flowEdges,
    isFetching,
    error,
    focusKey,
    isSimulating,
    handleNodeOpen,
    expandNotice,
  } = useRelationshipGraph(open ? anchor : null, handlers);

  return (
    <>
      <RelationshipGraphDrawerTheme />
      <Drawer
        title={
          <PortalHeader
            title={
            <GraphDrawerTitle>
              <strong>{anchor?.label || "this record"}</strong>
            </GraphDrawerTitle>
            }
            onClose={onClose}
          />
        }
        rootClassName="relationship-graph-drawer"
        width={DRAWER_WIDTH.relationshipGraph}
        open={open}
        onClose={onClose}
        closable={false}
        keyboard
        destroyOnClose
        styles={{
          header: { padding: 0 },
          body: { padding: 0, overflow: "hidden" },
        }}
      >
        <GraphDrawerBody>
          {error ? (
            <Alert
              type="error"
              showIcon
              message="Could not load relationships"
              description={error?.data?.detail || "Try again in a moment."}
              style={{ margin: "12px 16px 0" }}
            />
          ) : null}
          <Suspense fallback={<div style={{ padding: 24, color: "#94a3b8" }}>Loading graph…</div>}>
            <RelationshipGraph
              nodes={flowNodes}
              edges={flowEdges}
              isLoading={isFetching}
              isSimulating={isSimulating}
              focusKey={focusKey}
              onOpenNode={handleNodeOpen}
              expandNotice={expandNotice}
            />
          </Suspense>
        </GraphDrawerBody>
      </Drawer>
    </>
  );
}
